import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormWrapper, FormSchemas } from '../../../components/FormComponent';
import ButtonComponent from '../../../components/customButton';
import styles from '../../../styles/login/rightSection.module.scss';
import { MFA_COPY, MFA_UI } from '../../../constants/mfaCopy';
import { useSnackbar } from '../../../utils/useSnackbar';
import { useDispatch, useSelector } from 'react-redux';
import { resendMfa, verifyMfa } from '../../../features/auth/authSlice';
import { STATUS } from '../../../utils/api/sharedfunction';

const MfaOtpForm = ({
  onBack,
  emailHint = '',
}) => {
  const formRef = useRef(null);
  const { loading } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const [cooldownSeconds, setCooldownSeconds] = useState(MFA_UI.resendCooldownSeconds);
  const [otpValue, setOtpValue] = useState('');
  const { showSnackbar } = useSnackbar();

  const otpFields = useMemo(() => [
    {
      name: 'otp',
      type: 'text',
      label: MFA_COPY.otpLabel,
      placeholder: MFA_COPY.otpPlaceholder,
      required: true,
      autoFocus: true,
      maxLength: MFA_UI.otpLength,
      autoComplete: 'one-time-code',
      inputMode: 'numeric',
      pattern: '[0-9]*',
      customActionOnChange: (_name, value) => {
        const digits = String(value ?? '').replace(/\D/g, '').slice(0, MFA_UI.otpLength);
        if (digits !== value) {
          formRef.current?.setValue('otp', digits, { shouldValidate: true, shouldDirty: true });
        }
        setOtpValue(digits);
      },
    },
  ], []);

  useEffect(() => {
    if (cooldownSeconds <= 0) return undefined;
    const timerId = setTimeout(() => {
      setCooldownSeconds((prev) => prev - 1);
    }, 1000);
    return () => clearTimeout(timerId);
  }, [cooldownSeconds]);

  const handleFormSubmit = () => {
    if (formRef.current) {
      formRef.current.submit();
    }
  };

  const shouldRestartLogin = (errorText) =>
    String(errorText || '').includes(MFA_COPY.invalidChallengeMessage) ||
    /restart login/i.test(String(errorText || '')) ||
    /expired/i.test(String(errorText || ''));

  const handleConfirmSubmit = async ({ otp }) => {
    const response = await dispatch(verifyMfa(otp));
    if (response?.status === STATUS.SUCCESS) return;

    if (shouldRestartLogin(response?.error)) {
      onBack();
    }
  };

  async function handleResend() {
    if (cooldownSeconds > 0) return;
    const response = await dispatch(resendMfa());
    if (response?.status === STATUS.SUCCESS) {
      showSnackbar(response.message || MFA_COPY.resendSuccessMessage, 'success');
      setCooldownSeconds(MFA_UI.resendCooldownSeconds);
      return;
    }

    if (shouldRestartLogin(response?.error)) {
      onBack();
    }
  }

  const canResend = cooldownSeconds <= 0;
  const canVerify = otpValue.length === MFA_UI.otpLength;

  return (
    <div className={styles['mfa-form']}>
      <div className={styles['form-header']}>
        <h6 className={styles['form-title']}>
          {MFA_COPY.title}
        </h6>
        <p className={styles['form-subtitle']}>
          {MFA_COPY.subtitlePrefix}{' '}
          <span className={styles['mfa-email-hint']}>
            {emailHint || MFA_COPY.subtitleFallbackEmail}
          </span>
        </p>
      </div>

      <FormWrapper
        ref={formRef}
        schema={FormSchemas.mfaOtp}
        fields={otpFields}
        onSubmit={handleConfirmSubmit}
        defaultValues={{ otp: '' }}
        className={styles['mfa-form-wrapper']}
      />

      <div className={styles['mfa-resend-section']}>
        <p className={styles['mfa-resend-prompt']}>{MFA_COPY.resendPrompt}</p>
        {canResend ? (
          <button
            type="button"
            className={styles['mfa-resend-link']}
            onClick={handleResend}
          >
            {MFA_COPY.resendAction}
          </button>
        ) : (
          <p className={styles['mfa-resend-countdown']}>
            {MFA_COPY.resendCountdown(cooldownSeconds)}
          </p>
        )}
      </div>

      <div className={styles['mfa-actions']}>
        <ButtonComponent
          ctaText={MFA_COPY.verifyButton}
          variant="primary"
          className={`white bg-blue600 ${styles['mfa-action-button']}`}
          onClick={handleFormSubmit}
          disabled={loading || !canVerify}
          loading={loading}
        />
        <ButtonComponent
          ctaText={MFA_COPY.backToSignIn}
          variant="outline"
          className={`primary ${styles['mfa-action-button']}`}
          onClick={onBack}
          disabled={loading}
        />
      </div>
    </div>
  );
};

export default MfaOtpForm;
